import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { FieldError } from "../../components/common/States";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError, COMPANY_KEY } from "../../lib/api";
import { Field, validateAccount } from "./Register";
import { CompanyFieldset, BillingFieldset, validateCompany, EMPTY_COMPANY } from "../../components/company/CompanyFields";
import LogoPicker from "../../components/company/LogoPicker";

const AccountFieldset = ({ acc, setAcc, errors }) => {
  const set = (k) => (e) => setAcc({ ...acc, [k]: e.target.value });
  return (
    <fieldset className="grid gap-4 sm:grid-cols-2">
      <legend className="mb-3 font-display font-semibold">Votre compte administrateur</legend>
      <Field id="register-name" label="Nom complet" value={acc.name} onChange={set("name")} error={errors.name} />
      <Field id="register-email" label="Email de connexion" type="email" value={acc.email} onChange={set("email")} error={errors.acc_email} />
      <Field id="register-password" label="Mot de passe" type="password" value={acc.password} onChange={set("password")} error={errors.password} />
      <Field id="register-password-confirm" label="Confirmer" type="password" value={acc.confirm} onChange={set("confirm")} error={errors.confirm} />
    </fieldset>
  );
};

export default function CompanySignup() {
  const { user, authenticate, refresh } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY_COMPANY);
  const [acc, setAcc] = useState({ name: "", email: "", password: "", confirm: "" });
  const [logo, setLogo] = useState(null);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validateCompany(f);
    if (!user) { const a = validateAccount(acc); if (a.email) a.acc_email = a.email; delete a.email; Object.assign(e, a); }
    if (!terms) e.terms = "Vous devez accepter les conditions d'utilisation";
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Vérifiez les champs signalés"); return; }
    setLoading(true);
    try {
      const body = { ...f, accept_terms: terms };
      const data = user ? (await api.post("/companies", body)).data
        : await authenticate("/auth/register-company", { ...body, account: { name: acc.name, email: acc.email, password: acc.password } });
      localStorage.setItem(COMPANY_KEY, data.company_id);
      if (logo) {
        const fd = new FormData(); fd.append("file", logo);
        try { await api.post("/company/logo", fd); } catch (err) { toast.error(`Logo non enregistré : ${formatApiError(err)}`); }
      }
      await refresh();
      toast.success("Espace créé. Confirmez l'email professionnel pour soumettre votre demande.");
      navigate("/app/parametres", { replace: true });
    } catch (err) { setErrors({ form: formatApiError(err) }); } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-14">
      <p className="eyebrow">Espace entreprise</p>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{user ? "Créer un nouvel espace entreprise" : "Ouvrir l'espace de votre organisation"}</h1>
      <p className="mt-2 text-sm text-slate-500">Étapes : inscription (brouillon) → confirmation de l'email professionnel → validation par l'équipe VOXA AFRIKA → espace actif.</p>
      <form onSubmit={submit} className="card-surface mt-8 space-y-8 p-6 sm:p-8" noValidate data-testid="company-signup-form">
        <div><p className="mb-3 font-display font-semibold">Logo</p><LogoPicker file={logo} onChange={setLogo} /></div>
        <CompanyFieldset f={f} setF={setF} errors={errors} />
        <BillingFieldset f={f} setF={setF} errors={errors} />
        {user ? <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="signup-logged-as">Vous serez propriétaire de ce nouvel espace avec votre compte <b>{user.email}</b>.</p>
          : <AccountFieldset acc={acc} setAcc={setAcc} errors={errors} />}
        <div>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="signup-terms-checkbox" className="mt-0.5" />
            <span>J'accepte les <Link to="/conditions" target="_blank" className="text-blue-600 underline">conditions d'utilisation</Link> et la <Link to="/confidentialite" target="_blank" className="text-blue-600 underline">politique de confidentialité</Link>, et certifie être habilité(e) à représenter cette organisation.</span>
          </label>
          <FieldError>{errors.terms}</FieldError>
        </div>
        <FieldError>{errors.form && <span data-testid="company-register-error">{errors.form}</span>}</FieldError>
        <div className="flex flex-col-reverse items-center justify-between gap-4 sm:flex-row">
          {!user && <Link to="/connexion" className="text-sm text-slate-500 hover:text-slate-900" data-testid="company-register-login-link">J'ai déjà un compte</Link>}
          <Button type="submit" className="ml-auto h-11 rounded-full px-8" disabled={loading} data-testid="company-register-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Créer l'espace</Button>
        </div>
      </form>
    </div>
  );
}
